/**
 * Validates US phone number format
 * Valid format: +1 (XXX) XXX-XXXX
 * Example: +1 (775) 980-2006
 */
export const validateUSPhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber) return true; // Empty is allowed (optional field in most cases)

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, "");

  // Check if it has exactly 11 digits and starts with 1 (US country code)
  // Or 10 digits (without country code)
  if (digits.length === 11 && digits.startsWith("1")) {
    return true;
  }

  if (digits.length === 10) {
    return true;
  }

  return false;
};

/**
 * Alias for validateUSPhoneNumber for backward compatibility
 */
export const validatePhoneNumber = validateUSPhoneNumber;

/**
 * Formats phone number input to restrict invalid characters
 * Allows only digits, +, spaces, parentheses, and hyphens
 */
export const formatPhoneInput = (value: string): string => {
  return value.replace(/[^\d\s()+-]/g, "");
};

/**
 * Formats phone number with US mask: +1 (XXX) XXX-XXXX
 * Automatically adds +1 prefix and formats as user types
 * Can accept either formatted string or just digits
 */
export const formatPhoneNumber = (value: string | number): string => {
  // Convert to string and remove all non-digit characters
  const digits = String(value).replace(/\D/g, "");

  // If empty, return empty (allow user to clear the field completely)
  if (!digits) return "";

  // Get digits after country code (remove leading 1 if present)
  const phoneDigits = digits.startsWith("1") ? digits.slice(1) : digits;

  // Limit to 10 digits for US phone
  const limitedDigits = phoneDigits.slice(0, 10);

  // If no digits after removing country code, return empty
  if (limitedDigits.length === 0) {
    return "";
  }

  // Start with +1
  let formatted = "+1";

  // Add area code: +1 (XXX
  formatted += " (";
  formatted += limitedDigits.slice(0, 3);

  // Close area code and add next 3 digits: +1 (XXX) XXX
  if (limitedDigits.length >= 3) {
    formatted += ") ";
    formatted += limitedDigits.slice(3, 6);
  }

  // Add last 4 digits: +1 (XXX) XXX-XXXX
  if (limitedDigits.length >= 6) {
    formatted += "-";
    formatted += limitedDigits.slice(6, 10);
  }

  return formatted;
};

/**
 * Returns the maximum length for a phone number based on its format
 * US format with mask: +1 (XXX) XXX-XXXX = 18 characters
 */
export const getMaxPhoneLength = (_phoneNumber: string): number => {
  return 18; // +1 (XXX) XXX-XXXX
};

/**
 * Formats a valid US phone number for display
 * Example: +17759802006 -> +1 (775) 980-2006
 */
export const formatUSPhoneForDisplay = (phoneNumber: string): string => {
  if (!phoneNumber) return "";

  const digits = phoneNumber.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) {
    // Format: +1 (XXX) XXX-XXXX
    return `+${digits[0]} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  return phoneNumber;
};

/**
 * Removes mask from phone number and returns clean format for API
 * Example: +1 (775) 980-2006 -> +17759802006
 */
export const cleanPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return "";

  // Remove all non-digit characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, "");

  // Ensure it starts with +1
  const digits = cleaned.replace(/\D/g, "");
  if (digits.startsWith("1") && digits.length === 11) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }

  return cleaned;
};
